import React, { FC, forwardRef, useMemo, useState } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';

import getWrappedDisplayName from '../../../utils/hocs/getWrappedDisplayName';

const idTypes: Record<string, number> = {};

interface WithUniqueIdOptions {
  acceptOverride?: boolean;
}

type WithUniqueIdProps<Name extends string> = Record<Name, string>;

const withUniqueId = <Name extends string>(idName: Name, { acceptOverride }: WithUniqueIdOptions = {}) => (WrappedComponent: FC<WithUniqueIdProps<Name>>) => {
  idTypes[idName] = idTypes[idName] || 0;

  const WithUniqueId = forwardRef<unknown, WithUniqueIdProps<Name>>((props, ref) => {
    const [id] = useState(() => {
      if (acceptOverride && props[idName]) return props[idName];
      idTypes[idName] += 1;
      return idTypes[idName];
    });

    const idProps = useMemo(() => ({
      [idName]: id,
    }), [id]);

    return <WrappedComponent {...props} {...idProps} ref={ref} />;
  });

  WithUniqueId.displayName = getWrappedDisplayName('WithUniqueId', WrappedComponent);

  return hoistNonReactStatics(WithUniqueId, WrappedComponent);
};

export default withUniqueId;
